import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Phone,
  MessageSquare,
  Send,
  ExternalLink,
  CheckCircle2,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { useRealEstateContext } from "../providers/RealEstateContext";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { WhatsAppMessage } from "@/types/realEstate";
import {
  applyWhatsAppChatPresetBody,
  normalizeWhatsAppChatPresets,
} from "@/types/realEstate";
import {
  draftWhatsAppReply,
  isCampaignWhatsAppMessage,
  type ContactTimelineItem,
  type LeadAssistInput,
} from "./contactModel";
import {
  DEFAULT_REENGAGE_TEMPLATE,
  DEFAULT_REENGAGE_TEMPLATE_LANG,
} from "@/lib/whatsapp";

const CARE_WINDOW_MS = 24 * 60 * 60 * 1000;

interface ContactTelephonyDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactName: string;
  contactPhone?: string;
  contactId?: string | number;
  contact?: LeadAssistInput["contact"];
  timeline?: ContactTimelineItem[];
  initialTab?: "call" | "whatsapp";
}

export const ContactTelephonyDrawer: React.FC<ContactTelephonyDrawerProps> = ({
  open,
  onOpenChange,
  contactName,
  contactPhone = "",
  contactId,
  contact,
  timeline = [],
  initialTab = "whatsapp",
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { triggerWhatsAppMessage } = useRealEstateContext();
  const config = useConfigurationContext() as {
    whatsappChatPresets?: unknown;
  };

  const templates = useMemo(
    () =>
      normalizeWhatsAppChatPresets(config.whatsappChatPresets).map(
        (preset) => ({
          title: preset.title,
          text: applyWhatsAppChatPresetBody(preset.body, contactName),
        }),
      ),
    [config.whatsappChatPresets, contactName],
  );

  const [activeTab, setActiveTab] = useState<"call" | "whatsapp">(initialTab);
  const [waMessage, setWaMessage] = useState<string>("");
  const [callStatus, setCallStatus] = useState<
    "idle" | "calling" | "connected"
  >("idle");
  const [disposition, setDisposition] = useState<string>("Interested");
  const [notes, setNotes] = useState<string>("");
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
  const [savingLog, setSavingLog] = useState(false);
  const [threadMessages, setThreadMessages] = useState<WhatsAppMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sendingMeta, setSendingMeta] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const prefilledOnOpenRef = useRef(false);
  /** Prevents double mark-as-read for the same open contact session. */
  const markedReadForRef = useRef<string | null>(null);

  const lastInboundBody = useMemo(() => {
    const fromThread = [...threadMessages]
      .reverse()
      .find((m) => m.direction === "INBOUND")?.message_body;
    return fromThread?.trim() || undefined;
  }, [threadMessages]);

  const lastInboundAt = useMemo(() => {
    const inbound = [...threadMessages]
      .reverse()
      .find((m) => m.direction === "INBOUND");
    if (!inbound?.created_at) return null;
    const t = new Date(inbound.created_at).getTime();
    return Number.isFinite(t) ? t : null;
  }, [threadMessages]);

  /** Free-form Cloud API only works inside Meta's 24h customer-care window. */
  const withinCareWindow = useMemo(() => {
    if (lastInboundAt == null) return false;
    return Date.now() - lastInboundAt < CARE_WINDOW_MS;
  }, [lastInboundAt]);

  const hasUnreadInbound = useMemo(
    () =>
      threadMessages.some(
        (m) =>
          m.direction === "INBOUND" &&
          m.status !== "read" &&
          !isCampaignWhatsAppMessage(m),
      ),
    [threadMessages],
  );

  const defaultGreeting = useMemo(
    () =>
      `Namaste ${contactName}! Thank you for connecting with us regarding our projects.`,
    [contactName],
  );

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      prefilledOnOpenRef.current = false;
    } else {
      setWaMessage("");
      prefilledOnOpenRef.current = false;
    }
  }, [open, initialTab]);

  useEffect(() => {
    if (!open || prefilledOnOpenRef.current || waMessage.trim()) return;
    if (!contact) return;

    if (hasUnreadInbound) {
      const draft = draftWhatsAppReply({
        contact,
        timeline,
        lastInboundWhatsAppBody: lastInboundBody,
      });
      setWaMessage(draft);
      prefilledOnOpenRef.current = true;
    } else if (!loadingThread && threadMessages.length === 0) {
      setWaMessage(defaultGreeting);
      prefilledOnOpenRef.current = true;
    }
  }, [
    open,
    contact,
    timeline,
    lastInboundBody,
    hasUnreadInbound,
    waMessage,
    loadingThread,
    threadMessages.length,
    defaultGreeting,
  ]);

  const handleSuggestReply = () => {
    if (!contact) {
      setWaMessage(defaultGreeting);
      return;
    }
    setWaMessage(
      draftWhatsAppReply({
        contact,
        timeline,
        lastInboundWhatsAppBody: lastInboundBody,
        needsWhatsAppReply: hasUnreadInbound,
      }),
    );
  };

  const loadThread = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (contactId == null || !dataProvider) return;
      const silent = opts?.silent === true;
      if (!silent) setLoadingThread(true);
      try {
        const { data } = await dataProvider.getList<WhatsAppMessage>(
          "whatsapp_messages",
          {
            filter: { contact_id: contactId },
            sort: { field: "created_at", order: "ASC" },
            pagination: { page: 1, perPage: 50 },
          },
        );
        setThreadMessages(data.filter((m) => !isCampaignWhatsAppMessage(m)));
      } catch {
        if (!silent) setThreadMessages([]);
      } finally {
        if (!silent) setLoadingThread(false);
      }
    },
    [contactId, dataProvider],
  );

  const markInboundAsRead = useCallback(async () => {
    if (contactId == null || !dataProvider) return;
    const contactKey = String(contactId);
    if (markedReadForRef.current === contactKey) return;
    // Lock for this open session so concurrent effects don't double-update.
    markedReadForRef.current = contactKey;

    try {
      const { data } = await dataProvider.getList<WhatsAppMessage>(
        "whatsapp_messages",
        {
          filter: {
            contact_id: contactId,
            direction: "INBOUND",
            "status@eq": "unread",
          },
          sort: { field: "created_at", order: "DESC" },
          pagination: { page: 1, perPage: 50 },
        },
      );
      const unread = data.filter((m) => !isCampaignWhatsAppMessage(m));
      if (unread.length === 0) return;

      await Promise.all(
        unread.map((m) =>
          dataProvider.update("whatsapp_messages", {
            id: m.id,
            data: { status: "read" },
            previousData: m,
          }),
        ),
      );

      setThreadMessages((prev) =>
        prev.map((m) =>
          m.direction === "INBOUND" && m.status === "unread"
            ? { ...m, status: "read" }
            : m,
        ),
      );

      // Refresh Inbox list + badge / desk-queue counts after marking read.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["whatsapp_messages"] }),
      ]);
    } catch (e) {
      // Allow retry on the next open / tab switch if the update failed (e.g. RLS).
      markedReadForRef.current = null;
      console.warn("Failed to mark WhatsApp messages as read", e);
    }
  }, [contactId, dataProvider, queryClient]);

  useEffect(() => {
    if (!open || contactId == null) {
      markedReadForRef.current = null;
      return;
    }
    loadThread();

    const supabase = getSupabaseClient();
    const channel = supabase
      .channel(`contact-wa-${contactId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "whatsapp_messages",
          filter: `contact_id=eq.${contactId}`,
        },
        () => loadThread({ silent: true }),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [open, contactId, loadThread]);

  // Mark inbound unread → read when the agent opens the WhatsApp thread
  // (after messages load). Matches inbox UX: viewing the thread clears unread.
  useEffect(() => {
    if (!open || contactId == null || activeTab !== "whatsapp") return;
    if (loadingThread) return;
    void markInboundAsRead();
  }, [open, contactId, activeTab, loadingThread, markInboundAsRead]);

  useEffect(() => {
    if (open && threadMessages.length > 0) {
      threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [open, threadMessages]);

  const handleApplyTemplate = (text: string) => {
    setWaMessage(text);
  };

  const handleSendWhatsAppWeb = () => {
    if (!contactPhone) {
      notify("No phone number available for this contact", { type: "warning" });
      return;
    }
    triggerWhatsAppMessage(contactPhone, waMessage, true, contactId);
    void markInboundAsRead();
    notify("Opened WhatsApp Web with message!", { type: "info" });
    onOpenChange(false);
  };

  const handleSendMetaApi = async () => {
    if (!contactPhone) {
      notify("No phone number available for this contact", { type: "warning" });
      return;
    }
    if (!withinCareWindow) {
      notify(
        `Free-form WhatsApp is blocked outside the 24h window (Meta #131047). Send the re-engage template (${DEFAULT_REENGAGE_TEMPLATE}) first, or wait for the contact to reply.`,
        { type: "warning" },
      );
      return;
    }
    if (!waMessage.trim()) {
      notify("Message cannot be empty", { type: "warning" });
      return;
    }
    setSendingMeta(true);
    try {
      await triggerWhatsAppMessage(contactPhone, waMessage, false, contactId);
      notify("WhatsApp message accepted by Meta (delivery pending)", {
        type: "success",
      });
      setWaMessage("");
      await loadThread();
      await markInboundAsRead();
    } catch (e) {
      notify(
        `Failed to send WhatsApp message: ${e instanceof Error ? e.message : "unknown error"}`,
        { type: "warning" },
      );
    } finally {
      setSendingMeta(false);
    }
  };

  const handleSendReengageTemplate = async () => {
    if (!contactPhone) {
      notify("No phone number available for this contact", { type: "warning" });
      return;
    }
    setSendingMeta(true);
    try {
      await triggerWhatsAppMessage(
        contactPhone,
        "",
        false,
        contactId,
        "agent",
        null,
        {
          templateName: DEFAULT_REENGAGE_TEMPLATE,
          templateLanguage: DEFAULT_REENGAGE_TEMPLATE_LANG,
        },
      );
      notify(
        `Re-engage template "${DEFAULT_REENGAGE_TEMPLATE}" sent. After the contact replies, free-form chat works for 24h.`,
        { type: "success" },
      );
      await loadThread();
    } catch (e) {
      notify(
        `Template send failed: ${e instanceof Error ? e.message : "unknown error"}`,
        { type: "warning" },
      );
    } finally {
      setSendingMeta(false);
    }
  };

  const handleTriggerCall = () => {
    if (!contactPhone) {
      notify("No phone number available for this contact", { type: "warning" });
      return;
    }
    setCallStatus("calling");
    setCallStartedAt(Date.now());
    // Softphone / Exotel bridge is not wired in-app; mark connected for disposition logging.
    setTimeout(() => {
      setCallStatus("connected");
    }, 1500);
  };

  const handleEndAndLogCall = async () => {
    setSavingLog(true);
    const durationSec = callStartedAt
      ? Math.max(1, Math.round((Date.now() - callStartedAt) / 1000))
      : 0;
    const dispositionText = notes.trim()
      ? `${disposition} — ${notes.trim()}`
      : disposition;

    try {
      if (contactId != null && dataProvider) {
        const numericId = Number(contactId);
        await dataProvider.create("call_logs", {
          data: {
            contact_id: Number.isFinite(numericId) ? numericId : contactId,
            call_duration_seconds: durationSec,
            call_disposition: dispositionText,
          },
        });
        await dataProvider.update("contacts", {
          id: Number.isFinite(numericId) ? numericId : contactId,
          data: { last_seen: new Date().toISOString() },
          previousData: { id: contactId },
        });
        notify(`Call log saved for ${contactName}`, { type: "success" });
      } else {
        notify(
          `Call noted for ${contactName} (not persisted — missing contact id)`,
          { type: "warning" },
        );
      }
    } catch (e) {
      notify(
        `Could not save call log: ${e instanceof Error ? e.message : "unknown error"}`,
        { type: "warning" },
      );
    } finally {
      setSavingLog(false);
      setCallStatus("idle");
      setCallStartedAt(null);
      setNotes("");
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md flex flex-col h-full overflow-y-auto gap-0">
        <SheetHeader className="pb-3 pr-8 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            Engage {contactName}
          </SheetTitle>
          <SheetDescription>
            Target Phone:{" "}
            <span className="font-semibold text-foreground">
              {contactPhone || "Not provided"}
            </span>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 flex flex-col min-h-0 py-4">
          <div className="flex gap-2 mb-4 border-b pb-3">
            <Button
              variant={activeTab === "whatsapp" ? "default" : "outline"}
              size="sm"
              className="flex-1 gap-1.5"
              onClick={() => setActiveTab("whatsapp")}
            >
              <MessageSquare className="w-4 h-4" />
              WhatsApp
            </Button>
            <Button
              variant={activeTab === "call" ? "default" : "outline"}
              size="sm"
              className="flex-1 gap-1.5"
              onClick={() => setActiveTab("call")}
            >
              <Phone className="w-4 h-4" />
              Cloud Call
            </Button>
          </div>

          {activeTab === "whatsapp" && (
            <div className="flex-1 flex flex-col gap-4 min-h-0">
              <div className="flex-1 min-h-[160px] max-h-[240px] overflow-y-auto rounded-lg border bg-muted/40 p-3 space-y-2.5">
                {loadingThread && threadMessages.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    Loading messages…
                  </p>
                ) : threadMessages.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    No WhatsApp messages yet. Send one below.
                  </p>
                ) : (
                  threadMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === "OUTBOUND" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm shadow-xs ${
                          msg.direction === "OUTBOUND"
                            ? msg.status === "failed"
                              ? "bg-red-600 text-white rounded-br-md"
                              : "bg-emerald-600 text-white rounded-br-md"
                            : "bg-background border rounded-bl-md"
                        }`}
                      >
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {msg.message_body}
                        </p>
                        <p
                          className={`text-[10px] mt-1.5 ${
                            msg.direction === "OUTBOUND"
                              ? msg.status === "failed"
                                ? "text-red-100"
                                : "text-emerald-100"
                              : "text-muted-foreground"
                          }`}
                        >
                          {msg.direction}
                          {msg.status ? ` · ${msg.status}` : ""}
                          {" · "}
                          {new Date(msg.created_at).toLocaleString("en-IN", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={threadEndRef} />
              </div>

              {templates.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    {translate(
                      "resources.contacts.assist.whatsapp_chat_presets",
                      {
                        _: "Quick presets",
                      },
                    )}
                  </label>
                  <div className="flex flex-col gap-1.5">
                    {templates.map((tmpl, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="justify-start text-xs font-normal h-auto py-2.5 px-3 text-left"
                        onClick={() => handleApplyTemplate(tmpl.text)}
                      >
                        {tmpl.title}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {!loadingThread && !withinCareWindow && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
                  <div className="flex gap-2 text-sm text-amber-900 dark:text-amber-100">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <p>
                      Outside Meta&apos;s 24h customer-care window
                      {lastInboundAt
                        ? " (no inbound reply in the last 24 hours)"
                        : " (no inbound reply yet)"}
                      . Free-form API messages will fail with{" "}
                      <span className="font-mono">#131047</span>. Send an
                      approved template to re-engage, then free-form works after
                      they reply.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 border-amber-600 text-amber-900 dark:text-amber-100"
                    disabled={sendingMeta}
                    onClick={handleSendReengageTemplate}
                  >
                    <Send className="w-3.5 h-3.5" />
                    {sendingMeta
                      ? "Sending template…"
                      : `Send re-engage template (${DEFAULT_REENGAGE_TEMPLATE})`}
                  </Button>
                </div>
              )}

              <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Message Body
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={handleSuggestReply}
                  >
                    <Sparkles className="w-3 h-3" />
                    {translate("resources.contacts.assist.suggest_reply")}
                  </Button>
                </div>
                <Textarea
                  value={waMessage}
                  onChange={(e) => setWaMessage(e.target.value)}
                  placeholder="Type your WhatsApp message…"
                  className="min-h-[120px] text-sm resize-none bg-background border shadow-xs focus-visible:ring-emerald-500/30"
                  disabled={!withinCareWindow}
                />
              </div>

              <div className="flex flex-col gap-2.5 mt-auto pt-2 pb-1">
                <Button
                  onClick={handleSendMetaApi}
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={sendingMeta || !withinCareWindow}
                >
                  <Send className="w-4 h-4" />
                  {sendingMeta
                    ? "Sending…"
                    : withinCareWindow
                      ? "Send Direct via Meta API"
                      : "Meta free-form locked (outside 24h)"}
                </Button>
                <Button
                  onClick={handleSendWhatsAppWeb}
                  variant="outline"
                  className="w-full gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950"
                >
                  <ExternalLink className="w-4 h-4" />
                  Launch WhatsApp Web / App
                </Button>
              </div>
            </div>
          )}

          {activeTab === "call" && (
            <div className="flex-1 flex flex-col gap-4">
              <div className="bg-slate-50 dark:bg-slate-900 border rounded-lg p-4 text-center flex flex-col items-center gap-3">
                {callStatus === "idle" && (
                  <>
                    <div className="p-3 bg-emerald-100 dark:bg-emerald-950 rounded-full text-emerald-600">
                      <Phone className="w-6 h-6" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Call logging stub — no telephony provider is wired yet
                      (e.g. Exotel). Use this to save a disposition after an
                      external call.
                    </p>
                    <Button
                      onClick={handleTriggerCall}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      Log a Call
                    </Button>
                  </>
                )}

                {callStatus === "calling" && (
                  <>
                    <div className="p-3 bg-amber-100 dark:bg-amber-950 rounded-full text-amber-600 animate-pulse">
                      <Phone className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-semibold text-amber-600">
                      Preparing call log…
                    </p>
                  </>
                )}

                {callStatus === "connected" && (
                  <>
                    <div className="p-3 bg-blue-100 dark:bg-blue-950 rounded-full text-blue-600">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-semibold text-blue-600">
                      Ready — save disposition when the call is done
                    </p>
                  </>
                )}
              </div>

              {callStatus === "connected" && (
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                      Call Disposition
                    </label>
                    <Select value={disposition} onValueChange={setDisposition}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Interested">
                          Interested / Hot Lead
                        </SelectItem>
                        <SelectItem value="Site Visit Scheduled">
                          Site Visit Scheduled
                        </SelectItem>
                        <SelectItem value="Follow-up Needed">
                          Follow-up Needed
                        </SelectItem>
                        <SelectItem value="Not Interested">
                          Not Interested / Out of Budget
                        </SelectItem>
                        <SelectItem value="Wrong Number">
                          Wrong Number
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                      Call Notes
                    </label>
                    <Textarea
                      placeholder="Enter discussion summary..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="h-24 text-sm"
                    />
                  </div>

                  <Button
                    onClick={handleEndAndLogCall}
                    variant="destructive"
                    className="w-full"
                    disabled={savingLog}
                  >
                    {savingLog ? "Saving…" : "End & Save Call Log"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
